/**
 * Vague 20 — Index des Cartographies 360°
 *
 * Page d'accueil des 360° : liste les sujets déjà scannés (via Phantom Detector),
 * permet d'accéder rapidement à leur cartographie. Si rien n'a été scanné encore,
 * propose un point d'entrée pour aller scanner depuis Origination / Annuaire / Risques.
 *
 * Réutilise GET /workspace/cartographie/phantoms/aggregate qui retourne top_subjects.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layers, Loader2, ChevronRight, Sparkles, ArrowUpRight, Ghost, Building2, Shield,
} from "lucide-react";
import { api } from "@/lib/api";
import { fmtError } from "@/lib/notify";

const TYPE_META = {
  opportunite: { label: "Opportunité", icon: Sparkles, color: "#378ADD" },
  entite: { label: "Entité", icon: Building2, color: "#A78BFA" },
  risque: { label: "Risque", icon: Shield, color: "#EF4444" },
};

export default function Cartographie360Index() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/workspace/cartographie/phantoms/aggregate")
      .then((r) => setData(r.data))
      .catch((e) => setError(fmtError(e, "Index indisponible")))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-zinc-500 text-sm py-8 text-center" data-testid="carto360-index-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
      </div>
    );
  }
  if (error) return <div className="text-red-400 text-xs">{error}</div>;
  if (!data) return null;

  const subjects = data.top_subjects || [];

  return (
    <div className="space-y-5" data-testid="carto360-index-page">
      <p className="text-zinc-400 text-xs max-w-3xl">
        La <strong>Cartographie 360°</strong> est une vue x-ray d'un sujet (opportunité, entité, risque)
        avec sa position fractale, ses relations, ses frictions, ses plans et les <strong>fantômes</strong>
        détectés par NOAM. Cliquez un sujet pour ouvrir sa carte.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="carto360-index-kpis">
        <KPI label="Sujets scannés" value={data.total_subjects_scanned} color="#A78BFA" />
        <KPI label="Fantômes détectés" value={data.total_phantoms} color="#7F77DD" />
        <KPI label="Coût mensuel" value={data.total_cost_monthly_meur ? `${data.total_cost_monthly_meur.toFixed(2)} M€` : "—"} color="#EF4444" />
        <KPI label="Coût annuel" value={data.total_cost_annual_meur ? `${data.total_cost_annual_meur.toFixed(1)} M€` : "—"} color="#EF4444" />
      </div>

      {subjects.length === 0 ? (
        <div className="noam-card p-8 text-center" data-testid="carto360-index-empty">
          <Layers className="w-8 h-8 text-zinc-600 mx-auto" />
          <div className="text-sm text-zinc-300 mt-3">Aucune cartographie 360° scannée</div>
          <p className="text-xs text-zinc-500 mt-2 max-w-md mx-auto mb-3">
            Pour créer une cartographie, allez sur une opportunité, une entité ou un risque,
            puis cliquez sur le bouton <strong className="text-[#A78BFA]">360°</strong>.
          </p>
          <div className="flex items-center gap-2 justify-center flex-wrap">
            <button onClick={() => navigate("/app/origination")} className="text-xs px-3 py-1.5 rounded border border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/10">
              Aller au pipeline
            </button>
            <button onClick={() => navigate("/app/annuaire")} className="text-xs px-3 py-1.5 rounded border border-[#A78BFA]/40 text-[#A78BFA] hover:bg-[#A78BFA]/10">
              Aller à l'annuaire
            </button>
            <button onClick={() => navigate("/app/risques")} className="text-xs px-3 py-1.5 rounded border border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10">
              Aller aux risques
            </button>
          </div>
        </div>
      ) : (
        <div className="noam-card p-4" data-testid="carto360-index-list">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1">
            <Ghost className="w-3 h-3 text-[#A78BFA]" />
            Sujets scannés ({subjects.length}) — ordre : score fantôme décroissant
          </div>
          <div className="space-y-1.5">
            {subjects.map((s, i) => {
              const meta = TYPE_META[s.subject_type] || { label: s.subject_type, icon: Layers, color: "#7F77DD" };
              const Icon = meta.icon;
              return (
                <button
                  key={i}
                  onClick={() => navigate(`/app/cartographie/${s.subject_type}/${s.subject_id}`)}
                  className="w-full text-left text-xs flex items-center gap-3 p-3 bg-white/[0.02] border border-white/5 rounded hover:border-[#A78BFA]/40 transition group"
                  data-testid={`carto360-index-item-${i}`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" style={{ color: meta.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-200 font-medium truncate">{s.subject_label || "(sans nom)"}</div>
                    <div className="text-[10px] text-zinc-500 mt-0.5">
                      {meta.label} · {s.phantoms_count} fantôme(s)
                      {s.total_cost_monthly_meur > 0 ? ` · ${s.total_cost_monthly_meur.toFixed(2)} M€/m` : ""}
                      {s.scanned_at ? ` · scanné ${new Date(s.scanned_at).toLocaleDateString("fr-CH")}` : ""}
                    </div>
                  </div>
                  <div
                    className="px-2 py-1 rounded text-xs font-bold flex-shrink-0"
                    style={{ background: `${meta.color}1A`, color: meta.color }}
                  >
                    {s.phantom_score}
                  </div>
                  <ArrowUpRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-[#A78BFA] flex-shrink-0" />
                  <ChevronRight className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <div className="noam-card p-3">
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-base font-bold mt-1 truncate" style={{ color }}>{value}</div>
    </div>
  );
}
